import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import UploadDownloadPreview from "./Preview";
import { UploadDownloadContainer, UploadDownloadHeader } from "./style";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import { useTranslation } from "react-i18next";
import TooltipDialogCst from "@components/TooltipDialogCst";
import { DownloaderUploaderRef, PropsTypes } from "./types";
import TooltipInput from "@components/InputCst/TooltipInput";
import { UPLOAD_STATES } from "@components/UploaderCst/types";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import AssociationLibraryCst from "@components/AssociationLibraryCst";
import { MediaType } from "src/interfaces/Media";
import { ServicesURL } from "@utils/utilsApi";
import { MethodType } from "src/services/type";
import { fetcher } from "src/services/config";
import { useParams } from "react-router-dom";

const DownloaderUploaderCst = forwardRef<DownloaderUploaderRef, PropsTypes>(
  (
    {
      accept,
      maxSize = null,
      onChange,
      name = "Upload",
      disabled = false,
      onTemplateDownload,
      onDelete,
      onDownload,
      textTooltip,
      tooltipFile,
      tooltipFileIta,
      tooltipFileEn,
      required,
      downloadDisabled,
      fileName,
      type = [],
      showError,
      errorMessage,
      uploadButtonId,
      uploadButtonTestId,
      uploadButtonAriaLabel,
      uploadAlsoFromMediaLibrary = false,
    },
    ref,
  ) => {
    const theme = useTheme();
    const [error, setError] = useState<boolean | string>(false);
    const [localFile, setLocalFile] = useState<
      { name: string; type: string; source: string; id: number } | undefined
    >();
    const [fileState, setFileState] = React.useState<UPLOAD_STATES | null>(
      null,
    );

    const { t } = useTranslation(["service"]);

    const { id = "1" } = useParams();

    const deleteFile = () => {
      setLocalFile(undefined);
      onDelete && onDelete(localFile);
    };

    const inputClick = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => ({
      reset: () => {
        setLocalFile(undefined);
        setError(false);
        setFileState(null);
        if (inputClick.current) inputClick.current.value = "";
      },
    }));

    const onSelection = (file: any) => {
      if (file && file.length > 0) {
        setFileState(UPLOAD_STATES.PROGRESS);
        // console.log("File object from local:", file);

        if (
          accept?.includes("*") ||
          (file[0].type && accept?.includes(file[0].type))
        ) {
          setLocalFile({
            name: file[0].name,
            type: file[0].type,
            source: "local",
            id: 0,
          });
          onChange && onChange(file[0]);
          setTimeout(() => {
            setFileState(UPLOAD_STATES.DONE);
            setTimeout(() => {
              setFileState(null);
            }, 1500);
          }, 600);
          setError(false);
        } else {
          setFileState(null);
          setError("formatError");
          setLocalFile(undefined);
        }

        if (!!maxSize && file[0].size > Number(maxSize) * 1000000) {
          setFileState(null);
          setError("sizeError");
          setLocalFile(undefined);
        }
      }
    };

    const onButtonClick = (e: any) => {
      inputClick &&
        inputClick?.current &&
        (inputClick.current.type = "file") &&
        inputClick?.current.click();
    };

    const [fileAssociation, setFileAssociation] = useState<boolean | undefined>(
      undefined,
    );

    const handleLibraryFile = async (associatedFile: any) => {
      setFileState(UPLOAD_STATES.PROGRESS);

      // console.log("Associated file from library:", associatedFile); // Controlla l'oggetto associato

      const response = await fetcher(
        `${ServicesURL.getMediaManagerFile}?mediaId=${associatedFile[0].id}&corporateId=${id}&generateUrl=true`,
        MethodType.GET,
        undefined,
      );

      // console.log("Response from media library:", response); // Controlla la risposta

      const url = response?.output[0]?.url?.public_url;
      if (!url) {
        setFileState(null);
        setError("downloadError: url not found");
        return;
      }

      const originalFileName = response?.output[0]?.metadata?.originalFileName;
      if (!originalFileName) {
        setFileState(null);
        setError("downloadError: originalFileName not found");
        return;
      }

      const downloadResponse = await fetch(url);
      if (!downloadResponse.ok) {
        setFileState(null);
        setError("downloadError: failed to fetch file");
        return;
      }

      const blob = await downloadResponse.blob();

      const file = new File([blob], originalFileName, {
        type: blob.type || "application/octet-stream",
      });

      // console.log("File object from library:", file); // Controlla il tipo del file

      if (!!maxSize && file.size > Number(maxSize) * 1000000) {
        setFileState(null);
        setError("sizeError");
        setLocalFile(undefined);
        return;
      }

      const dt = new DataTransfer();
      dt.items.add(file);

      if (inputClick.current) {
        inputClick.current.files = dt.files;
      }

      setLocalFile({
        name: file.name,
        type: file.type,
        source: "library",
        id: associatedFile.id,
      });

      setFileState(UPLOAD_STATES.DONE);
      setError(false);

      onChange && onChange(file);
    };

    return (
      <Box sx={{ position: "relative", width: "100%" }}>
        {textTooltip && (
          <TooltipDialogCst
            inputDialog
            title={"Info"}
            textTooltip={textTooltip}
            disabled={disabled}
          />
        )}

        {tooltipFile && (
          <TooltipInput
            inputDialog
            hrefInput
            hrefIta={tooltipFileIta}
            hrefEn={tooltipFileEn}
            disabled={disabled}
          />
        )}
        {!!fileState && !!localFile && (
          <UploadDownloadPreview fileState={fileState} />
        )}
        <UploadDownloadContainer disabled={disabled}>
          <div style={{ display: "flex", padding: "2px 0" }}>
            {onTemplateDownload && type.includes("template") && (
              <>
                <IconButton onClick={() => onTemplateDownload()} size="small">
                  <Tooltip title={t("download-template")}>
                    <DescriptionOutlinedIcon />
                  </Tooltip>
                </IconButton>
              </>
            )}
            {onDownload && type.includes("download") && (
              <IconButton
                onClick={() => onDownload()}
                size="small"
                disabled={downloadDisabled || false}
              >
                <Tooltip title={t("download-file")}>
                  <FileDownloadIcon />
                </Tooltip>
              </IconButton>
            )}
            <IconButton
              size="small"
              disabled={disabled}
              {...(uploadButtonId ? { id: uploadButtonId } : {})}
              {...(uploadButtonTestId
                ? { "data-testid": uploadButtonTestId }
                : {})}
              {...(uploadButtonAriaLabel
                ? { "aria-label": uploadButtonAriaLabel }
                : {})}
            >
              <Tooltip title={t("upload")}>
                <>
                  <input
                    ref={inputClick}
                    onChange={(e: any) =>
                      onSelection(inputClick.current?.files)
                    }
                    type="file"
                    accept={accept}
                    style={{ display: "none" }}
                  />
                  <FileUploadIcon onClick={onButtonClick} />
                  {uploadAlsoFromMediaLibrary && (
                    <AddIcon onClick={() => setFileAssociation(true)} />
                  )}
                </>
              </Tooltip>
            </IconButton>
            {onDelete && (
              <IconButton
                disabled={disabled}
                onClick={() => deleteFile()}
                size="small"
              >
                <Tooltip title={t("delete")}>
                  <DeleteIcon />
                </Tooltip>
              </IconButton>
            )}
          </div>
          <UploadDownloadHeader disabled={disabled}>
            {required ? `${name} *` : name}
          </UploadDownloadHeader>
        </UploadDownloadContainer>
        <Box
          sx={{
            position: "absolute",
            left: "31px",
            top: "8px",
            width: "60%",
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
            maxWidth: "60%",
            color: disabled
              ? theme.palette.text.disabled
              : theme.palette.text.primary,
          }}
        >
          {localFile && localFile?.name ? localFile?.name : fileName}
        </Box>
        {(!!error || showError) && (
          <Typography
            color={theme.palette.error.main}
            sx={{ fontSize: "0.75rem" }}
          >
            {(error &&
              `${t(`${error}`)} ${maxSize ? `(max ${maxSize}MB)` : ``}`) ||
              (errorMessage ? errorMessage : null)}
          </Typography>
        )}
        {uploadAlsoFromMediaLibrary && fileAssociation && (
          <Dialog open={fileAssociation} maxWidth={"xl"}>
            <DialogContent>
              <AssociationLibraryCst
                association={
                  localFile?.id
                    ? { id: localFile.id, label: localFile.name }
                    : undefined
                }
                title={t("Associazione Badge")}
                filters={[{ field: "type", value: MediaType.OTHER }]}
                setAssociation={handleLibraryFile}
                setCloseAssociation={setFileAssociation}
              />
            </DialogContent>
          </Dialog>
        )}
      </Box>
    );
  },
);

export default DownloaderUploaderCst;
